/**
 * What the preview pane does with an object: the content kind (from the extension first, then the stored content
 * type), the Monaco language for text, and how many bytes to read for it. Images, audio and video render through Chromium
 * itself, so no viewer library ships with the app. PDF is known but not previewed (the app's window runs without
 * Chromium's PDF plugin): it is not read at all and offers Download.
 */

export type StoragePreviewKind = 'json' | 'markdown' | 'text' | 'svg' | 'image' | 'video' | 'audio' | 'pdf' | 'unknown';

export type StoragePreviewType = { kind: StoragePreviewKind; language: string; mime?: string };

const MiB = 1024 * 1024;
/** Text up to this size loads whole and can be edited. */
export const TEXT_FULL_READ_MAX = 5 * MiB;
/** A larger text file shows its first part, read-only. */
export const TEXT_HEAD_BYTES = 512 * 1024;
/** "Load the whole file" stays offered up to this size (read-only beyond `TEXT_FULL_READ_MAX`). */
export const TEXT_LOAD_ALL_MAX = 32 * MiB;
/** An unknown type reads this much to tell text from binary. */
export const UNKNOWN_SNIFF_BYTES = 256 * 1024;
const MEDIA_MAX: Partial<Record<StoragePreviewKind, number>> = { image: 32 * MiB, video: 64 * MiB, audio: 64 * MiB };

const text = (language: string): StoragePreviewType => ({ kind: 'text', language });
const media = (kind: StoragePreviewKind, mime: string): StoragePreviewType => ({ kind, language: 'plaintext', mime });

const BY_EXTENSION: Record<string, StoragePreviewType> = {
  json: { kind: 'json', language: 'json', mime: 'application/json' },
  geojson: { kind: 'json', language: 'json' },
  webmanifest: { kind: 'json', language: 'json' },
  har: { kind: 'json', language: 'json' },
  ipynb: { kind: 'json', language: 'json' },
  md: { kind: 'markdown', language: 'markdown' },
  markdown: { kind: 'markdown', language: 'markdown' },
  svg: { kind: 'svg', language: 'xml', mime: 'image/svg+xml' },
  txt: text('plaintext'), log: text('plaintext'), csv: text('plaintext'), tsv: text('plaintext'), jsonl: text('plaintext'),
  ndjson: text('plaintext'), env: text('ini'), ini: text('ini'), cfg: text('ini'), conf: text('ini'), properties: text('ini'),
  toml: text('ini'), yaml: text('yaml'), yml: text('yaml'), xml: text('xml'), xsd: text('xml'), xsl: text('xml'),
  xslt: text('xml'), config: text('xml'), csproj: text('xml'), props: text('xml'), targets: text('xml'), resx: text('xml'),
  plist: text('xml'), html: text('html'), htm: text('html'), css: text('css'), scss: text('scss'), less: text('less'),
  js: text('javascript'), mjs: text('javascript'), cjs: text('javascript'), jsx: text('javascript'), ts: text('typescript'),
  tsx: text('typescript'), py: text('python'), sql: text('sql'), sh: text('shell'), bash: text('shell'), ps1: text('powershell'),
  bat: text('bat'), cmd: text('bat'), cs: text('csharp'), java: text('java'), go: text('go'), rs: text('rust'), rb: text('ruby'),
  php: text('php'), kt: text('kotlin'), swift: text('swift'), c: text('cpp'), h: text('cpp'), cpp: text('cpp'), hpp: text('cpp'),
  r: text('r'), graphql: text('graphql'), proto: text('protobuf'), bicep: text('bicep'), tf: text('hcl'), dockerfile: text('dockerfile'),
  png: media('image', 'image/png'), jpg: media('image', 'image/jpeg'), jpeg: media('image', 'image/jpeg'), gif: media('image', 'image/gif'),
  webp: media('image', 'image/webp'), bmp: media('image', 'image/bmp'), ico: media('image', 'image/x-icon'), avif: media('image', 'image/avif'),
  apng: media('image', 'image/apng'),
  mp4: media('video', 'video/mp4'), m4v: media('video', 'video/mp4'), webm: media('video', 'video/webm'), ogv: media('video', 'video/ogg'),
  mov: media('video', 'video/quicktime'),
  mp3: media('audio', 'audio/mpeg'), wav: media('audio', 'audio/wav'), ogg: media('audio', 'audio/ogg'), oga: media('audio', 'audio/ogg'),
  opus: media('audio', 'audio/ogg'), m4a: media('audio', 'audio/mp4'), aac: media('audio', 'audio/aac'), flac: media('audio', 'audio/flac'),
  pdf: media('pdf', 'application/pdf'),
};

const BY_FILE_NAME: Record<string, StoragePreviewType> = { dockerfile: text('dockerfile'), makefile: text('plaintext'), '.gitignore': text('plaintext'), '.env': text('ini') };

const RENDERABLE_IMAGES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/avif', 'image/apng']);

function fromContentType(contentType: string | undefined): StoragePreviewType | undefined {
  const type = contentType?.split(';')[0].trim().toLowerCase();
  if (!type || type === 'application/octet-stream') return undefined;
  if (type === 'application/json' || type.endsWith('+json')) return { kind: 'json', language: 'json' };
  if (type === 'text/markdown') return { kind: 'markdown', language: 'markdown' };
  if (type === 'image/svg+xml') return { kind: 'svg', language: 'xml', mime: type };
  if (RENDERABLE_IMAGES.has(type)) return media('image', type);
  if (type.startsWith('video/')) return media('video', type);
  if (type.startsWith('audio/')) return media('audio', type);
  if (type === 'application/pdf') return media('pdf', type);
  if (type === 'application/xml' || type === 'text/xml' || type.endsWith('+xml')) return text('xml');
  if (type === 'text/html') return text('html');
  if (type === 'text/css') return text('css');
  if (type === 'application/javascript' || type === 'text/javascript') return text('javascript');
  if (type === 'application/x-yaml' || type === 'application/yaml' || type === 'text/yaml') return text('yaml');
  if (type.startsWith('text/')) return text('plaintext');
  return undefined;
}

/** The last path segment's extension, lower-cased ('' when there is none). */
export function fileExtension(name: string): string {
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

export function previewTypeOf(name: string, contentType?: string): StoragePreviewType {
  const base = name.slice(name.lastIndexOf('/') + 1).toLowerCase();
  return BY_FILE_NAME[base] ?? BY_EXTENSION[fileExtension(name)] ?? fromContentType(contentType) ?? { kind: 'unknown', language: 'plaintext' };
}

export const isTextKind = (kind: StoragePreviewKind): boolean => kind === 'json' || kind === 'markdown' || kind === 'text' || kind === 'svg';

/** The read plan for an object of `size` bytes: how much to read, or `tooLarge` when it is not previewed at all. */
export function previewReadPlan(kind: StoragePreviewKind, size: number, loadAll = false): { maxBytes: number } | { tooLarge: true; limit: number } | { noViewer: true } {
  if (kind === 'pdf') return { noViewer: true };
  if (isTextKind(kind)) {
    if (size <= TEXT_FULL_READ_MAX) return { maxBytes: Math.max(size, 1) };
    return loadAll && size <= TEXT_LOAD_ALL_MAX ? { maxBytes: size } : { maxBytes: TEXT_HEAD_BYTES };
  }
  if (kind === 'unknown') return { maxBytes: Math.max(1, Math.min(size, loadAll ? TEXT_LOAD_ALL_MAX : UNKNOWN_SNIFF_BYTES)) };
  const limit = MEDIA_MAX[kind] ?? TEXT_FULL_READ_MAX;
  return size <= limit ? { maxBytes: Math.max(size, 1) } : { tooLarge: true, limit };
}
